import Micromodal from 'micromodal';
import globals from '../common/globals';
import regexp from '../common/regexp';
import Events from '../lib/Events';

export function sleep(time) {
   return new Promise(resolve => setTimeout(resolve, time));
}

/** @param {HTMLElement} elem */
export function getCoords(elem) {
   const { left, top, width, height } = elem.getBoundingClientRect();

   return {
      top: top + pageYOffset,
      left: left + pageXOffset,
      bottom: top + pageYOffset + height,
      right: left + pageXOffset + width,
      width, height,
   };
}

export function isTouch() {
   return ('ontouchstart' in window) || navigator.maxTouchPoints;
}

// Copy of underscore escape
export const escape = (function () {
   const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '`': '&#x60;',
   };
   let escaper = function (match) {
      return map[match];
   };
   let source = '(?:' + Object.keys(map).join('|') + ')';
   let testRegexp = RegExp(source);
   let replaceRegexp = RegExp(source, 'g');

   return function (string) {
      string = string == null ? '' : '' + string;
      return testRegexp.test(string) ? string.replace(replaceRegexp, escaper) : string;
   };
}());

/** @returns {{[key: string]: string}} */
export function getGetParams(prepare = decodeURIComponent) {
   const search = location.search.slice(1);
   const getParams = {};

   if (!search) {
      return getParams;
   }

   search.split('&').forEach((item) => {
      const [key, val] = item.split('=', 2);
      getParams[key] = prepare(val);
   });

   return getParams;
}

/**
 * @param {String} html representing of a single element
 * @returns {HTMLElement}
 */
export function htmlToElem(html) {
   const template = document.createElement('template');
   template.innerHTML = html;

   return template.content.firstChild;
}

/** @param {'auto'|'manual'} value */
export function setScrollBehavior(value) {
   if (!('scrollRestoration' in history)) return;
   history.scrollRestoration = value;
}

export function isEmail(email) {
   return regexp.email.test(email);
}

/**
 * Clone the object using JSON method
 * 
 * p.s. this method can be used for JSON-like object only (e.g. API responses), 
 * but also this method is faster then loop based methods
 * (JSON much simpler the JS, so it can be parsed fast)
 * 
 * @template T
 * @param {T} object
 * @return {T}
 */
export function jsonClone(object) {
   return JSON.parse(JSON.stringify(object));
}

/**
 * Redirects to the url with a slight delay
 * 
 * Use cases:
 * 
 * - use it when you need to reload the page when closing a modal. 
 * 
 * The reason is that you can usually close the modal by the ESC button too, 
 * but pressing the ESC also can stop the redirect. So, you can write:
 * 
 * Micromodal.show('modal-id', { onClose: asyncRedirect('/some/path') });
 * 
 * @param {string} url 
 */
export function asyncRedirect(url) {
   requestAnimationFrame(() => location.href = url);
}

export function asyncReload() {
   requestAnimationFrame(() => location.reload());
}

/**
 * @param {HTMLElement} el 
 * @param {string} className 
 * @param {boolean} val 
 * 
 * @example
 * 
 * 
 * ```js
 * // instead of:
 * if (a === b) {
 *    $someEl.classList.add('visible');
 * } else {
 *    $someEl.classList.remove('visible');
 * }
 * //You can write:
 * setClassIf($someEl, 'visible', a === b)
 * ```
 */
export function setClassIf(el, className, val) {
   if (val) el.classList.add(className);
   else el.classList.remove(className);
}

export function decrypt(str, key) {
   return str.split('x')
      .filter(Boolean)
      .map(s => String.fromCharCode(parseInt(s, 16) ^ key))
      .join('');
}

export function isLocalStorageEnabled() {
   const testKey = new Date();

   try {
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);

      return true;
   } catch {
      return false;
   }
}

export function swapImgSources(img) {
   if (img.src || img.srcset) return;

   if (img.dataset.src) img.src = img.dataset.src;
   if (img.dataset.srcset) img.srcset = img.dataset.srcset;

   img.addEventListener('load', function () {
      img.classList.add('loaded');
   });

   if (!(img.parentElement instanceof HTMLPictureElement)) {
      return;
   }

   const sources = img.parentElement.querySelectorAll('source') || [];

   sources.forEach((source) => {
      if (source.dataset.srcset) source.srcset = source.dataset.srcset;
   });
}

/** 
 * @param {string} id
 * @param {import('micromodal').MicroModalConfig} config 
 */
export function showModal(id, config) {
   Micromodal.show(id, {
      ...globals.commonMicromodalConfig,
      ...config,
   });
}

export function hideModal(id) {
   Micromodal.close(id);
}

export const addStorageListener = (function () {
   const events = new Events();

   window.addEventListener('storage', ({ key, newValue }) => {
      if (key !== 'storage-message' || !newValue) return;
      const { type, data } = JSON.parse(newValue);
      events.emit(type, data);
   });

   /** 
    * @param {string} type 
    * @param {function} handler
    */
   function addStorageListener(type, handler) {
      events.addListener(type, handler);
   }

   return addStorageListener;
}());

/** @param {string} eventType */
export function emitStorageEvent(type, data) {
   if (!isLocalStorageEnabled()) return;

   const msg = JSON.stringify({ type, data });
   localStorage.setItem('storage-message', msg);
   localStorage.removeItem('storage-message');
}

/**
 * @param {T[]} src 
 * @param {number} size
 * @returns {T[][]}
 * @template T
 */
export function chunk(src, size) {
   const res = new Array(Math.ceil(src.length / size));

   for (let i = 0, j = 0; i < src.length; i += size, j++) {
      res[j] = src.slice(i, i + size);
   }

   return res;
}

export const transitionTurn = {
   off(el) {
      el.classList.add('no-transition');
   },
   on(el) {
      requestAnimationFrame(() => {
         el.classList.remove('no-transition');
      });
   },
};

export function forceReloadOnBack() {
   window.addEventListener('pageshow', ({ persisted }) => {
      const isBack = performance && performance.navigation.type === 2;
      if (persisted || isBack) location.reload();
   });
}

export function doOnBack(handler) {
   window.addEventListener('pageshow', ({ persisted }) => {
      const isBack = performance && performance.navigation.type === 2;
      if (persisted || isBack) handler();
   });
}

export function copyToClipboard(str) {
   const $ = document.createElement('textarea');
   $.style.cssText = 'position: absolute; left: -9999px';
   $.value = str;
   $.readOnly = true;
   document.body.appendChild($);
   $.select();
   document.execCommand('copy');
   document.body.removeChild($);
}